import React, { useState, useEffect } from 'react';
import { 
  Map, Target, FileText, Briefcase, Mic, Heart, Building, Plus,
  ChevronRight, X, Loader2, CheckCircle, Clock, AlertCircle, Play,
  Calendar, User, Filter, TrendingUp, Flag
} from 'lucide-react';
import { useUserEmail } from '../../context/UserAuthContext';

const API = typeof window !== 'undefined' ? window.location.origin : '';

const CATEGORIES = [
  { id: 'brand', label: 'Brand', icon: Target, color: 'bg-yellow-500' },
  { id: 'content', label: 'Content', icon: FileText, color: 'bg-blue-500' },
  { id: 'partnerships', label: 'Partnerships', icon: Briefcase, color: 'bg-purple-500' },
  { id: 'media', label: 'Media', icon: Mic, color: 'bg-cyan-500' },
  { id: 'community', label: 'Community', icon: Heart, color: 'bg-red-500' },
  { id: 'business', label: 'Business', icon: Building, color: 'bg-green-500' }
];

const STATUS_CONFIG = {
  planned: { label: 'Planned', color: 'bg-gray-500', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-500', icon: Play },
  completed: { label: 'Completed', color: 'bg-green-500', icon: CheckCircle },
  delayed: { label: 'Delayed', color: 'bg-red-500', icon: AlertCircle }
};

const PRIORITY_CONFIG = {
  high: { label: 'High', color: 'text-red-400', bgColor: 'bg-red-500/20' },
  medium: { label: 'Medium', color: 'text-yellow-400', bgColor: 'bg-yellow-500/20' },
  low: { label: 'Low', color: 'text-gray-400', bgColor: 'bg-gray-500/20' }
};

const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];

const AERoadmap = () => {
  const userEmail = useUserEmail();
  const [loading, setLoading] = useState(true);
  const [milestones, setMilestones] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [view, setView] = useState('timeline'); // timeline, list

  useEffect(() => {
    if (userEmail) {
      checkRole();
      fetchRoadmap();
    }
  }, [userEmail, selectedYear, filterCategory, filterStatus]);

  const checkRole = async () => {
    try {
      const res = await fetch(`${API}/api/always-elevate/user-role?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      setIsAdmin(data.role === 'admin');
      if (data.role === 'admin') {
        fetchStats();
      }
    } catch (err) {
      console.error('Error checking role:', err);
    }
  };

  const fetchRoadmap = async () => {
    try {
      let url = `${API}/api/always-elevate/roadmap?user_email=${encodeURIComponent(userEmail)}&year=${selectedYear}`;
      if (filterCategory !== 'all') {
        url += `&category=${filterCategory}`;
      }
      const res = await fetch(url);
      const data = await res.json();
      if (data.success) {
        let filtered = data.data;
        if (filterStatus !== 'all') {
          filtered = filtered.filter(m => m.status === filterStatus);
        }
        setMilestones(filtered);
      }
    } catch (err) {
      console.error('Error fetching roadmap:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/api/always-elevate/roadmap/stats?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      if (data.success) {
        setStats(data.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const getMilestonesByQuarter = (quarter) => {
    return milestones.filter(m => m.quarter === quarter);
  };

  const getCategoryIcon = (category) => {
    const cat = CATEGORIES.find(c => c.id === category);
    return cat?.icon || Target;
  };

  const getCategoryColor = (category) => {
    const cat = CATEGORIES.find(c => c.id === category);
    return cat?.color || 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C4A052]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="ae-roadmap-page">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-6">
        <div className="max-w-[1800px] mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[#C4A052] text-sm font-medium tracking-wider">
                {isAdmin ? 'ELLE MAXWELL AGENCY' : 'ALWAYS ELEVATE'}
              </p>
              <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: 'Anton, sans-serif' }} data-testid="ae-roadmap-title">
                Strategic Roadmap
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                {milestones.length} milestones for {selectedYear}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Year Selector */}
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="bg-black border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C4A052]"
                data-testid="year-selector"
              >
                <option value={2025}>2025</option>
                <option value={2026}>2026</option>
                <option value={2027}>2027</option>
              </select>
              
              {/* View Toggle */}
              <div className="flex items-center bg-white/5 rounded-lg p-1">
                <button
                  onClick={() => setView('timeline')}
                  className={`px-3 py-1.5 rounded text-sm ${view === 'timeline' ? 'bg-[#C4A052] text-black' : 'text-gray-400'}`}
                >
                  Timeline
                </button>
                <button
                  onClick={() => setView('list')}
                  className={`px-3 py-1.5 rounded text-sm ${view === 'list' ? 'bg-[#C4A052] text-black' : 'text-gray-400'}`}
                >
                  List
                </button>
              </div>
              
              {isAdmin && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#C4A052] text-black rounded-lg font-medium hover:bg-[#D4B062] transition-colors"
                  data-testid="create-milestone-btn"
                >
                  <Plus className="w-4 h-4" />
                  Add Milestone
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-6 py-8">
        {/* Stats Row (Admin Only) */}
        {isAdmin && stats && (
          <div className="grid grid-cols-4 gap-4 mb-8">
            <div className="bg-black border border-white/20 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <Map className="w-5 h-5 text-[#C4A052]" />
              </div>
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-gray-400">Total Milestones</p>
            </div>
            <div className="bg-black border border-white/20 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <p className="text-2xl font-bold">{stats.completed}</p>
              <p className="text-xs text-gray-400">Completed</p>
            </div>
            <div className="bg-black border border-white/20 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <Play className="w-5 h-5 text-blue-400" />
              </div>
              <p className="text-2xl font-bold">
                {stats.by_status?.find(s => s._id === 'in_progress')?.count || 0}
              </p>
              <p className="text-xs text-gray-400">In Progress</p>
            </div>
            <div className="bg-black border border-white/20 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-5 h-5 text-[#C4A052]" />
              </div>
              <p className="text-2xl font-bold">{stats.completion_rate}%</p>
              <p className="text-xs text-gray-400">Completion Rate</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          {/* Category Filters */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setFilterCategory('all')}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                filterCategory === 'all' ? 'bg-[#C4A052] text-black' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              All
            </button>
            {CATEGORIES.map(cat => {
              const Icon = cat.icon;
              return (
                <button
                  key={cat.id}
                  onClick={() => setFilterCategory(filterCategory === cat.id ? 'all' : cat.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                    filterCategory === cat.id ? `${cat.color} text-white` : 'bg-white/5 text-gray-400 hover:bg-white/10'
                  }`}
                >
                  <Icon className="w-3 h-3" />
                  {cat.label}
                </button>
              );
            })}
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm text-gray-400">Status:</span>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-black border border-white/20 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-[#C4A052]"
            >
              <option value="all">All</option>
              <option value="planned">Planned</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="delayed">Delayed</option>
            </select>
          </div>
        </div>

        {/* Timeline View */}
        {view === 'timeline' && (
          <div className="grid grid-cols-4 gap-4" data-testid="roadmap-timeline">
            {QUARTERS.map(quarter => {
              const quarterMilestones = getMilestonesByQuarter(quarter);
              return (
                <div key={quarter} className="bg-black border border-white/20 rounded-xl overflow-hidden">
                  {/* Quarter Header */}
                  <div className="bg-[#C4A052]/10 border-b border-white/10 px-4 py-3">
                    <h3 className="font-semibold text-[#C4A052]">{quarter} {selectedYear}</h3>
                    <p className="text-xs text-gray-400">{quarterMilestones.length} milestones</p>
                  </div>
                  
                  {/* Milestones */}
                  <div className="p-3 space-y-2 max-h-[500px] overflow-y-auto">
                    {quarterMilestones.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 text-sm">
                        No milestones
                      </div>
                    ) : (
                      quarterMilestones.map(milestone => {
                        const CategoryIcon = getCategoryIcon(milestone.category);
                        const statusConfig = STATUS_CONFIG[milestone.status] || STATUS_CONFIG.planned;
                        const StatusIcon = statusConfig.icon;
                        const priorityConfig = PRIORITY_CONFIG[milestone.priority] || PRIORITY_CONFIG.medium;
                        
                        return (
                          <div
                            key={milestone.id}
                            onClick={() => setSelectedMilestone(milestone)}
                            className="bg-black border border-white/20 rounded-lg p-3 cursor-pointer transition-colors"
                          >
                            <div className="flex items-start gap-2 mb-2">
                              <div className={`w-6 h-6 rounded ${getCategoryColor(milestone.category)} flex items-center justify-center flex-shrink-0`}>
                                <CategoryIcon className="w-3 h-3 text-white" />
                              </div>
                              <h4 className="font-medium text-sm line-clamp-2">{milestone.title}</h4>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className={`px-2 py-0.5 rounded text-xs ${statusConfig.color} text-white flex items-center gap-1`}>
                                <StatusIcon className="w-3 h-3" />
                                {statusConfig.label}
                              </span>
                              {milestone.priority === 'high' && (
                                <Flag className="w-3 h-3 text-red-400" />
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* List View */}
        {view === 'list' && (
          <div className="space-y-3" data-testid="roadmap-list">
            {milestones.length === 0 ? (
              <div className="bg-black border border-white/20 rounded-xl p-12 text-center">
                <Map className="w-16 h-16 mx-auto mb-4 text-gray-600" />
                <h3 className="text-lg font-medium text-gray-400 mb-2">No Milestones</h3>
                <p className="text-gray-500 text-sm">
                  No milestones match your filters
                </p>
              </div>
            ) : (
              milestones.map(milestone => {
                const CategoryIcon = getCategoryIcon(milestone.category);
                const statusConfig = STATUS_CONFIG[milestone.status] || STATUS_CONFIG.planned;
                const StatusIcon = statusConfig.icon;
                const priorityConfig = PRIORITY_CONFIG[milestone.priority] || PRIORITY_CONFIG.medium;
                
                return (
                  <div
                    key={milestone.id}
                    onClick={() => setSelectedMilestone(milestone)}
                    className="bg-black border border-white/20 rounded-xl p-5 cursor-pointer transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-xl ${getCategoryColor(milestone.category)} flex items-center justify-center flex-shrink-0`}>
                          <CategoryIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <div className="flex items-center gap-3">
                            <h3 className="font-semibold text-lg">{milestone.title}</h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color} text-white flex items-center gap-1`}>
                              <StatusIcon className="w-3 h-3" />
                              {statusConfig.label}
                            </span>
                            <span className={`px-2 py-1 rounded text-xs ${priorityConfig.bgColor} ${priorityConfig.color}`}>
                              {priorityConfig.label}
                            </span>
                          </div>
                          <p className="text-gray-400 text-sm mt-1 line-clamp-2">{milestone.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                            <span>{milestone.quarter} {milestone.year}</span>
                            <span className="capitalize">{milestone.category}</span>
                            {milestone.target_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Due {new Date(milestone.target_date).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-500" />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </main>

      {/* Detail Modal */}
      {selectedMilestone && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0f0f0f] border border-white/10 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl ${getCategoryColor(selectedMilestone.category)} flex items-center justify-center`}>
                  {(() => {
                    const CategoryIcon = getCategoryIcon(selectedMilestone.category);
                    return <CategoryIcon className="w-6 h-6 text-white" />;
                  })()}
                </div>
                <div>
                  <h3 className="text-xl font-bold">{selectedMilestone.title}</h3>
                  <p className="text-gray-400 text-sm capitalize">{selectedMilestone.category}</p>
                </div>
              </div>
              <button onClick={() => setSelectedMilestone(null)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Status & Priority */}
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_CONFIG[selectedMilestone.status]?.color || 'bg-gray-500'} text-white`}>
                  {STATUS_CONFIG[selectedMilestone.status]?.label || selectedMilestone.status}
                </span>
                <span className={`px-3 py-1 rounded text-sm ${PRIORITY_CONFIG[selectedMilestone.priority]?.bgColor} ${PRIORITY_CONFIG[selectedMilestone.priority]?.color}`}>
                  {PRIORITY_CONFIG[selectedMilestone.priority]?.label} Priority
                </span>
              </div>

              {/* Timeline */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-1">Quarter</h4>
                  <p>{selectedMilestone.quarter} {selectedMilestone.year}</p>
                </div>
                {selectedMilestone.target_date && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-1">Target Date</h4>
                    <p>{new Date(selectedMilestone.target_date).toLocaleDateString()}</p>
                  </div>
                )}
              </div>

              {/* Description */}
              {selectedMilestone.description && (
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Description</h4>
                  <p className="text-gray-300">{selectedMilestone.description}</p>
                </div>
              )}

              {/* Deliverables */}
              {selectedMilestone.deliverables && selectedMilestone.deliverables.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Deliverables</h4>
                  <ul className="space-y-1">
                    {selectedMilestone.deliverables.map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                        <CheckCircle className="w-4 h-4 text-[#C4A052]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Dependencies */}
              {selectedMilestone.dependencies && selectedMilestone.dependencies.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Dependencies</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedMilestone.dependencies.map((dep, i) => (
                      <span key={i} className="px-3 py-1 bg-white/10 rounded-full text-sm">
                        {dep}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Owner */}
              {selectedMilestone.owner && (
                <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                  <User className="w-5 h-5 text-[#C4A052]" />
                  <div>
                    <p className="text-xs text-gray-400">Owner</p>
                    <p className="text-sm">{selectedMilestone.owner.split('@')[0]}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-white/10 flex justify-end">
              <button
                onClick={() => setSelectedMilestone(null)}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal (Admin) */}
      {showCreateModal && isAdmin && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0f0f0f] border border-white/10 rounded-xl max-w-lg w-full">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h3 className="text-lg font-bold">Add Milestone</h3>
              <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-gray-400 text-sm">Milestone creation form coming soon.</p>
            </div>
            <div className="p-6 border-t border-white/10 flex justify-end">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AERoadmap;
